import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

type ToastType = "error" | "success";

interface Toast {
  type: ToastType;
  message: string;
  ts: number;
}

interface ToastContextValue {
  showError: (_message: string) => void;
  showSuccess: (_message: string) => void;
  dismiss: () => void;
  toast: Toast | null;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setToast(null);
  }, []);

  const showError = useCallback((message: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setToast({ type: "error", message, ts: Date.now() });
  }, []);

  const showSuccess = useCallback((message: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast({ type: "success", message, ts: Date.now() });
    timerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  // Global handler for uncaught promise rejections — surfaces as a toast so
  // users see *something* when an async path falls through without explicit
  // error handling.
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", event.reason);
      const message = event.reason?.message || "An unexpected error occurred.";
      if (message.length < 200) {
        setToast({ type: "error", message, ts: Date.now() });
      }
    };
    const handleGlobalError = (event: ErrorEvent) => {
      console.error("Unhandled error:", event.error);
    };
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleGlobalError);
    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleGlobalError);
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ showError, showSuccess, dismiss, toast }),
    [showError, showSuccess, dismiss, toast]
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

export function ToastViewport() {
  const { toast, dismiss } = useToast();
  if (!toast) return null;
  return (
    <div className={`toast ${toast.type === "error" ? "toast-error" : "toast-success"}`} role="alert">
      <span className="toast-message">{toast.message}</span>
      <button className="toast-close" onClick={dismiss} aria-label="Dismiss">&times;</button>
      {toast.type === "success" && <div className="toast-progress" key={toast.ts} />}
    </div>
  );
}
